// Recently used "Save to assets" destinations, per logged-in user, on this
// device. Kept in localStorage (the same backend-free posture as apiKey.ts) so
// the Save popover can offer the user's last few categories with the most
// recent preselected — no backend needed.

import { Destination } from './types';

const STORAGE_PREFIX = 'assetLab.magicStudio.recentDestinations';
const MAX_RECENT = 5;

function keyFor(userId: string): string {
  return `${STORAGE_PREFIX}.${userId || 'anon'}`;
}

function isDestination(value: unknown): value is Destination {
  if (!value || typeof value !== 'object') return false;
  const d = value as Record<string, unknown>;
  return (
    typeof d.projectId === 'number' &&
    typeof d.projectName === 'string' &&
    typeof d.categoryId === 'string' &&
    typeof d.categoryName === 'string' &&
    Array.isArray(d.categoryPath)
  );
}

// The user's recent destinations, newest first (at most MAX_RECENT). Returns []
// if storage is unavailable or holds nothing valid.
export function getRecentDestinations(userId: string): Destination[] {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isDestination).slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

// Record a destination as the most recently used: moves it to the front,
// de-duplicating by category, and caps the list. Returns the updated list.
export function pushRecentDestination(userId: string, dest: Destination): Destination[] {
  const next = [
    dest,
    ...getRecentDestinations(userId).filter((d) => d.categoryId !== dest.categoryId),
  ].slice(0, MAX_RECENT);
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(next));
  } catch {
    // ignore — nothing else we can do if storage is unavailable.
  }
  return next;
}
